/**
 * R5 — RP67 → RP68 (D30 : la partie « décider » limitée aux avenants ; D34) :
 * le client (MOA) lit la fiche de l'avenant et l'accepte ou le refuse avec un code de
 * vérification (SMS, sinon e-mail) — « Signature électronique par code SMS ».
 *
 * Affiché au client en tête de son chantier quand un avenant attend son accord
 * (aucun affichage sinon). Réutilisable par l'écran « Mon chantier » (R6, RP70).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Signature } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import AvenantFiche from './AvenantFiche';
import CodeVerificationDialog, { CODE_HEADER } from '../shared/CodeVerificationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AvenantsADecider({ chantierId, onDecided }) {
  const [items, setItems] = useState([]);
  const [action, setAction] = useState(null); // { avenant, decision }
  const [commentaire, setCommentaire] = useState('');
  const [codeOpen, setCodeOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/avenants`, { withCredentials: true });
      setItems((data.items || []).filter((a) => a.statut === 'PROPOSE'));
    } catch { setItems([]); }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  if (!items.length) return null;

  const demarrer = (avenant, decision) => {
    if (decision === 'refuser' && !commentaire.trim()) {
      setAction({ avenant, decision });
      toast.error("Indiquez la raison du refus : l'architecte en a besoin pour corriger l'avenant.");
      return;
    }
    setAction({ avenant, decision });
    setCodeOpen(true);
  };

  // Appelé par la fenêtre du code : renvoie l'erreur axios si le code est refusé
  const valider = async (code) => {
    const { avenant, decision } = action;
    const { data } = await axios.post(
      `${API_URL}/api/chantiers/${chantierId}/avenants/${avenant.id}/decision`,
      { decision, commentaire },
      { withCredentials: true, headers: { [CODE_HEADER]: code } },
    );
    toast.success(data.message || (decision === 'accepter' ? 'Avenant accepté' : 'Avenant refusé'));
    setAction(null);
    setCommentaire('');
    load();
    onDecided?.();
  };

  return (
    <section className="mb-6 space-y-3" data-testid="avenants-a-decider">
      <div>
        <h2 className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
          À décider : {items.length > 1 ? `${items.length} modifications du marché` : 'une modification du marché'}
        </h2>
        <p className="text-sm text-[#6B6B6B]">
          L'architecte vous soumet des travaux en plus ou en moins (avenant). Lisez la fiche, puis acceptez
          ou refusez : un code de vérification vous est envoyé par SMS ou par e-mail pour confirmer.
        </p>
      </div>
      {items.map((av) => (
        <AvenantFiche key={av.id} avenant={av} publicClient>
          <div className="border-t border-[#E0E0DB] px-4 py-3 space-y-2">
            <Textarea
              rows={2}
              value={action?.avenant?.id === av.id ? commentaire : ''}
              onFocus={() => { if (action?.avenant?.id !== av.id) { setAction({ avenant: av, decision: null }); setCommentaire(''); } }}
              onChange={(e) => { setAction({ avenant: av, decision: action?.decision || null }); setCommentaire(e.target.value); }}
              placeholder="Commentaire (obligatoire en cas de refus)"
              className="rounded-none text-sm"
              data-testid={`avenant-commentaire-${av.id}`}
            />
            <div className="flex flex-wrap gap-2 items-center">
              <Button className="rounded-none gap-2 bg-[#2D6A4F] hover:bg-[#225538] text-white"
                onClick={() => demarrer(av, 'accepter')} data-testid={`avenant-accepter-${av.id}`}>
                <CheckCircle size={16} weight="bold" /> Accepter
              </Button>
              <Button variant="outline" className="rounded-none gap-2 border-[#C53030] text-[#C53030]"
                onClick={() => demarrer(av, 'refuser')} data-testid={`avenant-refuser-${av.id}`}>
                <XCircle size={16} /> Refuser
              </Button>
              <span className="text-[11px] text-[#6B6B6B] inline-flex items-center gap-1">
                <Signature size={12} /> Signature électronique par code (envoyé par SMS ou par e-mail)
              </span>
            </div>
          </div>
        </AvenantFiche>
      ))}
      <CodeVerificationDialog
        open={codeOpen}
        onOpenChange={setCodeOpen}
        demanderUrl={`${API_URL}/api/acces/code`}
        demanderBody={{ action: 'avenant' }}
        titre={action?.decision === 'refuser' ? "Confirmer le refus de l'avenant" : "Confirmer l'accord sur l'avenant"}
        onValider={valider}
      />
    </section>
  );
}
